import { SVGIcon } from '@progress/kendo-svg-icons';
import { GDFPalletCode } from '../gdf/type';
import {
    BarbColorType,
    HeatmapStyle,
    IataNullReportColor,
    IataTurbulenceDisplayType,
    IataTurbulencePalette,
    MetarDisplayMode,
    WeatherLightningColor,
    WeatherSettings
} from '../settings/settings';
import { airmetsSigmets } from './airmetsSigmets';
import { airports } from './airports';
import { airspace } from './airspace';
import { convective } from './convective';
import { icing } from './icing';
import { lightning } from './lightning';
import { misc } from './misc';
import { radar } from './radar';
import { satellite } from './satellite';
import { turbulence } from './turbulence';
import { winds } from './winds';

export interface PaletteBase {
    name?: string;
    id: number;
    type: string;
    units?: string;
    suffix?: string;
    /** If true, this pallet will not be shown in the map legend menu */
    hideFromLegend?: boolean;
    /** If true, no tooltip will be shown for this palette  */
    noTooltip?: boolean;
    getValue?: (value: number) => number;
    /** If true, the tooltip will show in format of <value> instead of normal <from> to <to>.  TODO: make this mandatory and populate all pallets with it */
    tooltipSingleValue?: true;
    scale?: number; // Optional scale factor to apply to values when displaying in legend e.g. 1.3 for 130%
}

export interface DiscretePalette extends PaletteBase {
    type: 'discreteBar';
    values: Array<string | number | null>;
    colors: string[];
}

export interface GradientPalette extends PaletteBase {
    type: 'gradientBar';
    minValue: number;
    maxValue: number;
    maxDisplayValue?: number;
    minDisplayValue?: number;
    stops: Array<{ value: number; color: string; hideLabel?: boolean }>;
}

export interface CategoricalIndicatorPalette extends PaletteBase {
    type: 'categorical';
    withValues?: false;
    entries: Array<{ label: string; color?: string; icon?: SVGIcon; subtext?: string; hide?: boolean }>;
}

export interface CategoricalIndicatorPaletteWithValues extends PaletteBase {
    type: 'categorical';
    withValues: true;
    entries: Array<{ label: string; color?: string; icon?: SVGIcon; value: number; subtext?: string; hide?: boolean }>;
}

export interface CombinedPalette extends PaletteBase {
    type: 'combined';
    displayType: CategoricalIndicatorPalette['type'] | DiscretePalette['type'];
    colors: string[];
    values: Array<number | string>;
    palettes: Array<{ name: string; offset: number; length: number }>;
}

export type Palette =
    | DiscretePalette
    | GradientPalette
    | CategoricalIndicatorPalette
    | CategoricalIndicatorPaletteWithValues
    | CombinedPalette;

const palettes = Object.fromEntries(
    [
        ...radar,
        ...satellite,
        ...winds,
        ...convective,
        ...icing,
        ...turbulence,
        ...airmetsSigmets,
        ...airspace,
        ...misc,
        ...airports,
        ...lightning
    ].map((x) => [x.id, x])
) as Record<number, Palette>;

/**
 * @deprecated use {@link getPalette}
 */
export default palettes;

/**
 * This is very chaotic...
 * Each GDF file has multiple frames in it, each frame has palette code in the header.
 * Palette code should determine how that frame is drawn, but there are some products that don't have that code filled in or have a wrong palette code.
 * There are also product that share palette codes but are drawn differently depending on the product code.
 * There are also palette codes that should map to different palettes depending on the settings. E.g. volcash maps to volcash_dark or volcash_light depending on the map theme. Or metar that has different palettes depending on the display mode.
 * On top of that there are product that use multiple palettes to draw.
 *
 * getPalette method map palette code 1-1 to a palette. If a product needs more than 1 palette to draw it needs to get it manually because it's hard to break that 1-1 mapping assumption at this point. 
 * getLegendDisplayPalettes method returns an array of palettes that should be displayed in the map legend menu. E.g. volcash palette code will map palette for color coding volcanos and the palette for color coding ash clouds.
 *
 * TODO: those methods should also take in product id because the same palette code may map to different palette definition depending on which product is being rendered
 */

export function getPalette(id: GDFPalletCode, settings: WeatherSettings, isLight: boolean = true): Palette | null {
    if (id === GDFPalletCode.iataturb) {
        if (settings.turbulenceIcing.iataDisplayType === IataTurbulenceDisplayType.edr) {
            if (settings.turbulenceIcing.iataNullReportColor === IataNullReportColor.gray) {
                return palettes[GDFPalletCode.iata_turb_edr_gray];
            }
            return palettes[GDFPalletCode.iata_turb_edr_white];
        } else {
            if (settings.turbulenceIcing.iataSeverityPalette === IataTurbulencePalette.iata) {
                if (settings.turbulenceIcing.iataNullReportColor === IataNullReportColor.gray) {
                    return palettes[GDFPalletCode.iata_turb_severity_iata_gray];
                }
                return palettes[GDFPalletCode.iata_turb_severity_iata_white];
            }
            if (settings.turbulenceIcing.iataNullReportColor === IataNullReportColor.gray) {
                return palettes[GDFPalletCode.iata_turb_severity_industry_gray];
            }

            return palettes[GDFPalletCode.iata_turb_severity_industry_white];
        }
    }

    if (id === GDFPalletCode.winds) {
        if (settings.winds.heatmapStyle === HeatmapStyle.temperature) {
            return palettes[GDFPalletCode.temp_heatmap];
        }
        if (settings.winds.heatmapStyle === HeatmapStyle.winds || settings.winds.barbColorType === BarbColorType.intensity) {
            return palettes[GDFPalletCode.wind_heatmap];
        }
    }

    if (id === GDFPalletCode.lightning0) {
        if (settings.lightning.color === WeatherLightningColor.faded) {
            return palettes[GDFPalletCode.lightning0_faded];
        }
        return palettes[GDFPalletCode.lightning0_discrete];
    }

    if (id === GDFPalletCode.lightning1) {
        if (settings.lightning.color === WeatherLightningColor.faded) {
            return palettes[GDFPalletCode.lightning1_faded];
        }
        return palettes[GDFPalletCode.lightning1_discrete];
    }

    if (id === GDFPalletCode.metar) {
        if (
            settings.airport.displayMode === MetarDisplayMode.category ||
            settings.airport.displayMode === MetarDisplayMode.categorySky ||
            settings.airport.displayMode === MetarDisplayMode.categorySkyWind ||
            settings.airport.displayMode === MetarDisplayMode.categorySkyWindValues
        ) {
            return palettes[GDFPalletCode.metar];
        }
        if (settings.airport.displayMode === MetarDisplayMode.ceiling) {
            return palettes[GDFPalletCode.metar_ceiling];
        }
        if (settings.airport.displayMode === MetarDisplayMode.visibility) {
            return palettes[GDFPalletCode.metar_visibility];
        }
    }

    if (id === GDFPalletCode.probsevere) {
        return isLight ? palettes[GDFPalletCode.probsevere_light] : palettes[GDFPalletCode.probsevere_dark];
    }

    if (id === GDFPalletCode.volcash) {
        return palettes[isLight ? GDFPalletCode.volcash_light : GDFPalletCode.volcash_dark];
    }

    return palettes[id] ?? null;
}

export function getLegendDisplayPalettes(id: GDFPalletCode, settings: WeatherSettings, isLight: boolean = true): (Palette | null)[] {
    if (id === GDFPalletCode.volcash) {
        return [getPalette(id, settings, isLight), palettes[GDFPalletCode.volcash_cloud2]];
    }

    return [getPalette(id, settings, isLight)];
}
